import { Button } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import type { LiveUpdatesStack } from 'api/liveUpdates.types'
import ArrowRight from 'components/Icons/ArrowRight'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import ConnectionActionIcon from './ConnectionActionIcon'
import { TONE_COLORS } from './providerStatus'
import type { ProviderStatusContent } from './providerStatus'

const t = buildTranslate('live_updates.overview.status')

export interface ConnectionStatusBannerProps {
  stack: LiveUpdatesStack
  /** Resolved by OverviewTab, which also decides whether this banner shows
   * at all — it appears only while the connection still needs attention. */
  content: ProviderStatusContent
  onManageOloWebhook: () => void
}

// A compact, always-in-view summary of the same connection status
// ProviderStatusSection details further down the page (Sketch prototype
// live-updates-onboarding v29: a short banner directly under the page
// header, above the hero) — so a merchant scanning the top of the page
// already knows whether live updates are actually flowing.
const ConnectionStatusBanner = ({
  stack,
  content,
  onManageOloWebhook,
}: ConnectionStatusBannerProps) => {
  const [css] = useStyletron()

  // Nothing to name in the copy's %{provider} slot — render nothing rather
  // than a sentence with a hole in it.
  if (!stack.orderingProvider) return null

  const toneColors = TONE_COLORS[content.action.tone]
  const interpolation = { provider: stack.orderingProvider.displayName }

  return (
    <div
      role="status"
      data-testid="lu-overview-connection-banner"
      className={css({
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        flexWrap: 'wrap',
        padding: '14px 16px',
        borderRadius: '4px',
        border: `1px solid ${toneColors.border}`,
        backgroundColor: toneColors.background,
      })}
    >
      <span
        aria-hidden="true"
        className={css({ display: 'flex', flexShrink: 0 })}
      >
        <ConnectionActionIcon status={content.connectionStatus} />
      </span>
      <div className={css({ flex: '1 1 0%', minWidth: 0 })}>
        <Text.BodyText4
          bold
          color="grey90"
          className={css({ display: 'block' })}
        >
          {t(content.headingKey, interpolation)}
        </Text.BodyText4>
        <Text.BodyText5 color="grey70">
          {t(content.action.bodyKey, {
            ...interpolation,
            ...content.action.bodyInterpolation,
          })}
        </Text.BodyText5>
      </div>
      {content.action.ctaKey && (
        <Button kind="minimal" onClick={onManageOloWebhook}>
          <span
            className={css({
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
            })}
          >
            {t(content.action.ctaKey)}
            <ArrowRight fillColor={palette.belizeHole} />
          </span>
        </Button>
      )}
    </div>
  )
}

export default ConnectionStatusBanner
